/**
 * <s-summit-progress>
 * Interactive altitude mountain (blocks/summit-progress.liquid).
 * - The range slider [data-range] sets the altitude: updates --share (climber, slider fill),
 *   the gold trail [data-trail], the readout [data-readout], the next-camp line [data-next],
 *   [data-reached] on camps [data-camp] and [data-unlocked] on rewards [data-cost].
 * - Camp buttons jump the slider to that camp.
 * - On view, the climber animates from base camp up to the starting altitude (motion allowed).
 * - Fine pointers: back ridges drift with the pointer via --px / --py (-1..1).
 * Next-camp copy comes from data-to-go-template / data-summit-text / data-camp-names.
 */
class SSummitProgress extends HTMLElement {
  #ac = null;
  #observer = null;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.range = this.querySelector('[data-range]');
    this.max = Number(this.range?.max) || 1;
    this.camps = [...this.querySelectorAll('[data-camp]')];
    this.rewards = [...this.querySelectorAll('[data-cost]')];
    this.trail = this.querySelector('[data-trail]');
    this.readout = this.querySelector('[data-readout]');
    this.next = this.querySelector('[data-next]');

    this.range?.addEventListener('input', () => this.#set(Number(this.range.value)), { signal });
    for (const camp of this.camps) {
      camp.addEventListener('click', () => this.#animateTo(Number(camp.dataset.camp)), { signal });
    }

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce) {
      const start = Number(this.range?.value) || 0;
      if (start > 0) {
        this.#set(0);
        this.#observer = new IntersectionObserver(([entry]) => {
          if (!entry.isIntersecting) return;
          this.#observer.disconnect();
          this.#animateTo(start);
        }, { threshold: 0.35 });
        this.#observer.observe(this);
      }
      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.addEventListener('pointermove', (event) => this.#parallax(event), { signal });
        this.addEventListener('pointerleave', () => {
          this.style.setProperty('--px', '0');
          this.style.setProperty('--py', '0');
        }, { signal });
      }
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #parallax(event) {
    const rect = this.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = ((event.clientY - rect.top) / rect.height) * 2 - 1;
    this.style.setProperty('--px', x.toFixed(2));
    this.style.setProperty('--py', y.toFixed(2));
  }

  #animateTo(target) {
    cancelAnimationFrame(this.#frame);
    const from = Number(this.range?.value) || 0;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      this.#set(target);
      return;
    }
    const duration = 900;
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      this.#set(Math.round(from + (target - from) * eased));
      if (p < 1) this.#frame = requestAnimationFrame(step);
    };
    this.#frame = requestAnimationFrame(step);
  }

  #set(metres) {
    const value = Math.max(0, Math.min(metres, this.max));
    const share = (value / this.max) * 100;
    if (this.range) this.range.value = String(value);
    this.style.setProperty('--share', share.toFixed(2));
    this.trail?.setAttribute('stroke-dasharray', `${share.toFixed(2)} 100`);
    if (this.readout) this.readout.textContent = value.toLocaleString(document.documentElement.lang || 'de');

    let nextCamp = null;
    for (const camp of this.camps) {
      const alt = Number(camp.dataset.camp);
      camp.toggleAttribute('data-reached', value >= alt);
      if (!nextCamp && alt > value) nextCamp = camp;
    }
    for (const reward of this.rewards) {
      reward.toggleAttribute('data-unlocked', value >= Number(reward.dataset.cost));
    }
    if (this.next) {
      if (nextCamp) {
        const togo = (Number(nextCamp.dataset.camp) - value).toLocaleString(document.documentElement.lang || 'de');
        this.next.textContent = (this.dataset.toGoTemplate || '')
          .replace('__M__', togo)
          .replace('__C__', nextCamp.dataset.campName || '');
      } else {
        this.next.textContent = this.dataset.summitText || '';
      }
    }
  }
}

if (!customElements.get('s-summit-progress')) customElements.define('s-summit-progress', SSummitProgress);
